import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Url } from '@wix/da-url';
import { getCurrentUserHasCore, getIsVerificationNeeded } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { pushVerificationPopup } from '@wix/da-shared-react/pkg/VerificationPopup/actions';
import { useFirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/hooks';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags';
import { getCanSubmitFreePrompts, getCanSubmitPaidPrompts } from '../../redux/form/selectors';
import { generateFromPrompt, storeFormValuesForRedirectPersistence, pushPointsModal } from '../../redux/form/actions';
import { DreamupFormModals } from '../../constants';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
export var FormAction = /*#__PURE__*/ function(FormAction) {
    FormAction["DirectToPackagePicker"] = "direct-to-package-picker";
    FormAction["DisplayVerificationModal"] = "display-verification-modal";
    FormAction["DisplayPointsModal"] = "display-points-modal";
    FormAction["DisplayPromptGuidelinesModal"] = "display-prompt-guidelines-modal";
    FormAction["SubmitFreePrompt"] = "submit-free-prompt";
    FormAction["SubmitPaidPrompt"] = "submit-paid-prompt";
    FormAction["AnonJoin"] = "anon-join";
    /**
   * When a user is logged in, we use this action in placements that aren't the main dreamup app page.
   */ FormAction["RedirectToDreamupApp"] = "redirect-to-dreamup-app";
    /**
   * This form action is only used as a backup in case there is a bug somewhere. We'll take some sensible
   * action on submission and hope for the best.
   */ FormAction["Fallback"] = "fallback-action";
    return FormAction;
}({});
export function usePromptFormAction(/**
   * Some form placements are only meant as ingress points to dreamup. We've got no way to
   * determine this, so we have to rely on the user to pass it through.
   */ isIngress = false) {
    const dispatch = useDispatch();
    const { isLoggedIn } = useContext(SessionContext);
    const hasCore = useSelector(getCurrentUserHasCore);
    const { hasFlag: hasNotSeenPromptGuidelines } = useFirstTimeFlag(FirstTimeFlag.IsDreamupPromptGuidelinesNotSeen);
    const isVerificationNeeded = useSelector(getIsVerificationNeeded);
    const canSubmitFreePrompts = useSelector(getCanSubmitFreePrompts);
    const canSubmitPaidPrompts = useSelector(getCanSubmitPaidPrompts);
    let formAction = "fallback-action";
    if (!isLoggedIn) {
        formAction = "anon-join";
    } else if (isVerificationNeeded) {
        formAction = "display-verification-modal";
    } else if (isIngress) {
        formAction = "redirect-to-dreamup-app";
    } else if (hasNotSeenPromptGuidelines) {
        formAction = "display-prompt-guidelines-modal";
    } else if (!canSubmitFreePrompts && hasCore && !canSubmitPaidPrompts) {
        formAction = "display-points-modal";
    } else if (!canSubmitFreePrompts && !hasCore) {
        formAction = "direct-to-package-picker";
    } else if (canSubmitFreePrompts) {
        formAction = "submit-free-prompt";
    } else if (canSubmitPaidPrompts) {
        formAction = "submit-paid-prompt";
    }
    const handleFormSubmit = useCallback((e)=>{
        const submitPrompt = (usePoints)=>{
            dispatch(generateFromPrompt(usePoints));
        };
        switch(formAction){
            case "anon-join":
                dispatch(storeFormValuesForRedirectPersistence());
                break;
            case "redirect-to-dreamup-app":
                dispatch(storeFormValuesForRedirectPersistence());
                window.location.href = Url.dreamupLink({
                    persistFromStorage: true
                });
                break;
            case "direct-to-package-picker":
                window.location.href = Url.coreMembershipLink(Url.dreamupLink());
                break;
            case "display-points-modal":
                dispatch(pushPointsModal());
                break;
            case "display-verification-modal":
                dispatch(pushVerificationPopup());
                break;
            case "display-prompt-guidelines-modal":
                dispatch(pushModal(DreamupFormModals.PROMPT_GUIDELINES, {
                    wrapInModal: false,
                    params: {
                        // People in the prompt guidelines modal wil  all have free prompts left. They'll never
                        // be able to a prompt without clearing that FTUE flag, so it should be safe to pass through
                        // `false` for `usePoints`
                        onConfirm: ()=>submitPrompt(false)
                    }
                }));
                break;
            case "submit-paid-prompt":
                submitPrompt(true);
                break;
            case "submit-free-prompt":
                submitPrompt(false);
                break;
            // If our frontend checks don't work or there is a bug, we'll let the server tell us what we messed up.
            case "fallback-action":
            default:
                submitPrompt(false);
                break;
        }
        e === null || e === void 0 ? void 0 : e.preventDefault();
    }, [
        dispatch,
        formAction
    ]);
    return [
        handleFormSubmit,
        formAction
    ];
}

//# sourceMappingURL=usePromptFormAction.js.map